import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Bug, Settings, ToggleLeft, ToggleRight, RefreshCw, Trash2 } from 'lucide-react';

export default function AdminBugTracker({
  settings,
  setSettings,
  reports,
  statusCounts,
  filter,
  setFilter,
  onSaveSettings,
  savingSettings,
  onLoadReports,
  onUpdateBugStatus,
  updatingBug,
  onDeleteBug,
}) {
  const [selectedBug, setSelectedBug] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const handleOpenDetail = (bug) => {
    setSelectedBug({ ...bug });
    setShowDetailDialog(true);
  };

  const handleSaveChanges = async () => {
    if (selectedBug) {
      await onUpdateBugStatus(selectedBug.id, selectedBug.status, selectedBug.admin_notes);
      setShowDetailDialog(false);
    }
  };

  const handleDelete = () => {
    if (selectedBug) {
      onDeleteBug(selectedBug.id);
      setShowDetailDialog(false);
    }
  };

  return (
    <>
      <Card className="rounded-sm bg-slate-800 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-400" />
            Bug Tracker
            {statusCounts.new > 0 && (
              <Badge className="bg-red-600 ml-2">{statusCounts.new} new</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-slate-400">
            Manage bug reporting for beta testing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Settings Section */}
          <div className="p-4 bg-slate-700/50 rounded-sm space-y-4">
            <h4 className="font-medium text-slate-200 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </h4>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Enable Bug Reporting</Label>
                <p className="text-xs text-slate-400">Show bug report button to all users</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className="text-slate-300"
              >
                {settings.enabled ? (
                  <ToggleRight className="h-8 w-8 text-green-500" />
                ) : (
                  <ToggleLeft className="h-8 w-8 text-slate-500" />
                )}
              </Button>
            </div>

            <div>
              <Label className="text-slate-300 mb-2 block">Developer Email</Label>
              <Input
                type="email"
                value={settings.developer_email}
                onChange={(e) => setSettings({ ...settings, developer_email: e.target.value })}
                placeholder="developer@company.com"
                className="rounded-sm bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-400 mt-1">Receive email notifications for new bug reports</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Email Notifications</Label>
                <p className="text-xs text-slate-400">Send email when bugs are submitted</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSettings({ ...settings, email_notifications: !settings.email_notifications })}
                className="text-slate-300"
              >
                {settings.email_notifications ? (
                  <ToggleRight className="h-8 w-8 text-green-500" />
                ) : (
                  <ToggleLeft className="h-8 w-8 text-slate-500" />
                )}
              </Button>
            </div>

            <Button
              onClick={onSaveSettings}
              disabled={savingSettings}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              {savingSettings ? 'Saving...' : 'Save Bug Tracker Settings'}
            </Button>
          </div>

          {/* Reports List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-slate-200">Bug Reports</h4>
              <div className="flex gap-2">
                <Select 
                  value={filter.status || 'all'} 
                  onValueChange={(val) => setFilter({ ...filter, status: val === 'all' ? '' : val })}
                >
                  <SelectTrigger className="w-32 rounded-sm bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={onLoadReports} className="rounded-sm border-slate-600">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Status Summary */}
            <div className="flex gap-2 mb-4">
              <Badge className="bg-red-600">{statusCounts.new || 0} New</Badge>
              <Badge className="bg-yellow-600">{statusCounts.in_progress || 0} In Progress</Badge>
              <Badge className="bg-green-600">{statusCounts.resolved || 0} Resolved</Badge>
              <Badge className="bg-slate-600">{statusCounts.closed || 0} Closed</Badge>
            </div>

            {reports.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No bug reports yet</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {reports.map((bug) => (
                  <div
                    key={bug.id}
                    className="p-3 bg-slate-700 rounded-sm hover:bg-slate-600 cursor-pointer"
                    onClick={() => handleOpenDetail(bug)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${
                            bug.priority === 'critical' ? 'bg-red-500' :
                            bug.priority === 'high' ? 'bg-orange-500' :
                            bug.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />
                          <span className="font-medium text-slate-200">{bug.title}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-1">{bug.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                          <span>{bug.reporter_email}</span>
                          <span>{new Date(bug.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Badge className={
                        bug.status === 'new' ? 'bg-red-600' :
                        bug.status === 'in_progress' ? 'bg-yellow-600' :
                        bug.status === 'resolved' ? 'bg-green-600' : 'bg-slate-600'
                      }>
                        {bug.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bug Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-400" />
              Bug Report Details
            </DialogTitle>
          </DialogHeader>
          {selectedBug && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400 text-xs">Title</Label>
                <p className="font-medium">{selectedBug.title}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs">Priority</Label>
                  <Badge className={
                    selectedBug.priority === 'critical' ? 'bg-red-600' :
                    selectedBug.priority === 'high' ? 'bg-orange-600' :
                    selectedBug.priority === 'medium' ? 'bg-yellow-600' : 'bg-green-600'
                  }>
                    {selectedBug.priority}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Status</Label>
                  <Select
                    value={selectedBug.status}
                    onValueChange={(val) => setSelectedBug({ ...selectedBug, status: val })}
                  >
                    <SelectTrigger className="w-full mt-1 rounded-sm bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Description</Label>
                <p className="text-sm bg-slate-700 p-2 rounded-sm whitespace-pre-wrap">{selectedBug.description}</p>
              </div>
              {selectedBug.steps_to_reproduce && (
                <div>
                  <Label className="text-slate-400 text-xs">Steps to Reproduce</Label>
                  <p className="text-sm bg-slate-700 p-2 rounded-sm whitespace-pre-wrap">{selectedBug.steps_to_reproduce}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-400 text-xs">Reporter</Label>
                  <p>{selectedBug.reporter_name || selectedBug.reporter_email}</p>
                  <p className="text-xs text-slate-500">{selectedBug.reporter_company}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Submitted</Label>
                  <p>{new Date(selectedBug.created_at).toLocaleString()}</p>
                </div>
              </div>
              {selectedBug.page_url && (
                <div>
                  <Label className="text-slate-400 text-xs">Page URL</Label>
                  <p className="text-xs text-blue-400 break-all">{selectedBug.page_url}</p>
                </div>
              )}
              <div>
                <Label className="text-slate-400 text-xs">Admin Notes</Label>
                <Textarea
                  value={selectedBug.admin_notes || ''}
                  onChange={(e) => setSelectedBug({ ...selectedBug, admin_notes: e.target.value })}
                  placeholder="Add notes about this bug..."
                  className="mt-1 rounded-sm bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex justify-between pt-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="rounded-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-sm border-slate-600">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updatingBug}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {updatingBug ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
